<template>
  <!--
    "Help & Support" (ACHIEVERY-1363, Etapa 5). Solo código (no Prismic), contenido global.
    Izquierda: Video tutorials (abren VideoModal agnóstico). Derecha: FAQs (acordeón con CollapseTransition).
  -->
  <section class="help-support">
    <div class="help-support__head">
      <span class="help-support__eyebrow">{{ t.eyebrow }}</span>
      <h2 class="help-support__title">{{ t.title }}</h2>
      <p class="help-support__subtitle">{{ t.subtitle }}</p>
    </div>

    <div class="help-support__grid">
      <!-- Video tutorials -->
      <div class="hs-block">
        <div class="hs-block__title">
          <span class="hs-block__icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="3"/>
              <path d="M10 9l5 3-5 3z" fill="white" stroke="none"/>
            </svg>
          </span>
          <h3 class="hs-block__heading">{{ t.videosTitle }}</h3>
        </div>

        <div class="hs-tut-grid">
          <button
            v-for="video in content.videos"
            :key="video.id"
            type="button"
            class="hs-video"
            :data-label="`${t.videosTitle} - ${video.title}`"
            @click="openVideo(video)"
          >
            <span class="hs-video__thumb" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#252836">
                <path d="M6 4l14 8-14 8z"/>
              </svg>
            </span>
            <span class="hs-video__c">
              <span class="hs-video__title">{{ video.title }}</span>
              <span class="hs-video__dur">{{ video.duration }} {{ video.platform }}</span>
            </span>
          </button>
        </div>
      </div>

      <!-- FAQs -->
      <div class="hs-block">
        <div class="hs-block__title">
          <span class="hs-block__icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <path d="M12 17h.01"/>
            </svg>
          </span>
          <h3 class="hs-block__heading">{{ t.faqsTitle }}</h3>
        </div>

        <div class="hs-faqs">
          <div v-for="(faq, i) in content.faqs" :key="i" class="hs-faq">
            <button
              type="button"
              class="hs-faq__q"
              :aria-expanded="isFaqOpen(i) ? 'true' : 'false'"
              :aria-controls="`hs-faq-panel-${i}`"
              @click="toggleFaq(i)"
            >
              <span class="hs-faq__question">{{ faq.q }}</span>
              <span class="hs-faq__icon" :class="{ 'is-open': isFaqOpen(i) }" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#009fdb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 5v14"/>
                  <path d="M5 12h14"/>
                </svg>
              </span>
            </button>
            <CollapseTransition>
              <div v-if="isFaqOpen(i)" :id="`hs-faq-panel-${i}`" class="hs-faq__panel">
                <!-- La respuesta es HTML propio del repo (helpSupport.js): admite listas y links. -->
                <div class="hs-faq__answer" v-html="faq.a"></div>
              </div>
            </CollapseTransition>
          </div>
        </div>
      </div>
    </div>

    <!-- Montaje permanente: se abre/cierra con :show, NO con v-if (ver nota del portal en VideoModal). -->
    <VideoModal v-bind="videoModalProps" @close="activeVideo = null" />
  </section>
</template>

<script>
import CollapseTransition from './CollapseTransition.vue';
import VideoModal from './VideoModal.vue';
import helpSupportData from '../../data/helpSupport';

export default {
  name: 'HelpSupport',
  components: { CollapseTransition, VideoModal },
  props: {
    prismicLang: {
      type: String,
      required: true,
      validator: (value) => ['en-us', 'es-es'].includes(value)
    }
  },
  data() {
    return {
      openFaqs: [], // índices de FAQs abiertas (permite varias abiertas)
      activeVideo: null
    };
  },
  computed: {
    t() {
      const texts = {
        'en-us': {
          eyebrow: "We're here to help",
          title: 'Help & Support',
          subtitle: 'Short how-to videos and quick answers.',
          videosTitle: 'Video tutorials',
          faqsTitle: 'Frequently asked questions'
        },
        'es-es': {
          eyebrow: 'Estamos para ayudarte',
          title: 'Ayuda y soporte',
          subtitle: 'Videos cortos de ayuda y respuestas rápidas.',
          videosTitle: 'Video tutoriales',
          faqsTitle: 'Preguntas frecuentes'
        }
      };
      return texts[this.prismicLang] || texts['en-us'];
    },
    content() {
      return helpSupportData[this.prismicLang] || helpSupportData['en-us'];
    },
    // Props del modal, derivadas del video activo (sin video: cerrado y con campos vacíos).
    videoModalProps() {
      const video = this.activeVideo;
      return {
        show: !!video,
        type: video ? video.type : 'jwplayer',
        videoId: video ? video.videoId : '',
        title: video ? video.title : ''
      };
    }
  },
  methods: {
    toggleFaq(i) {
      const idx = this.openFaqs.indexOf(i);
      if (idx === -1) {
        this.openFaqs.push(i);
        // Analytics / GTM: evento al DESPLEGAR una pregunta (el cierre no se trackea).
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'faq_open',
          'button_text': (this.content.faqs[i] || {}).q || ''
        });
      } else {
        this.openFaqs.splice(idx, 1);
      }
    },
    isFaqOpen(i) {
      return this.openFaqs.includes(i);
    },
    openVideo(video) {
      // Analytics / GTM: el trigger de click por data-label no captura los <button>, así que
      // los videos empujan el evento explícitamente (mismo valor que el data-label).
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'click_element',
        label: `${this.t.videosTitle} - ${(video && video.title) || ''}`
      });
      if (video && video.videoId) {
        this.activeVideo = video;
      }
    }
  }
};
</script>
